import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  ChangeMatrixCell,
  MatrixAxis,
  MatrixCellStatus,
  PartTreeNode,
} from '../../../types/drbfm';

interface ChangeMatrixGridProps {
  parts: PartTreeNode[];
  axes: MatrixAxis[];
  cells: ChangeMatrixCell[];
  onSelectCell: (cell: ChangeMatrixCell) => void;
}

const cellStyles: Record<
  MatrixCellStatus,
  { variant: 'outline' | 'ghost'; className: string }
> = {
  unchecked: {
    variant: 'outline',
    className: 'border-warning text-warning hover:bg-warning/10 hover:text-warning',
  },
  none: { variant: 'ghost', className: '' },
  changed: {
    variant: 'outline',
    className: 'border-success text-success hover:bg-success/10 hover:text-success',
  },
  important_changed: {
    variant: 'outline',
    className:
      'border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive',
  },
  unknown: {
    variant: 'outline',
    className: 'border-warning text-warning hover:bg-warning/10 hover:text-warning',
  },
  not_applicable: { variant: 'ghost', className: '' },
};

const ChangeMatrixGrid: React.FC<ChangeMatrixGridProps> = ({
  parts,
  axes,
  cells,
  onSelectCell,
}) => {
  const { t } = useTranslation();

  const labels: Record<MatrixCellStatus, string> = {
    unchecked: t('drbfm.workflow.matrix.shortUnchecked', '未'),
    none: t('drbfm.workflow.matrix.shortNone', 'なし'),
    changed: t('drbfm.workflow.matrix.shortChanged', 'あり'),
    important_changed: t('common.important', '重要'),
    unknown: t('drbfm.workflow.matrix.shortUnknown', '不明'),
    not_applicable: '—',
  };

  const cellByKey = new Map(
    cells.map((cell) => [`${cell.partNodeId}:${cell.axisId}`, cell]),
  );

  return (
    <TooltipProvider>
      <div className="max-h-[560px] overflow-auto rounded-md border border-border">
        <table
          className="w-full caption-bottom text-sm"
          aria-label={t('drbfm.workflow.matrix.gridAriaLabel', '変化点抽出マトリクス')}
        >
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="min-w-[180px] text-foreground">{t('drbfm.workflow.matrix.part', '部品')}</TableHead>
              {axes.map((axis) => (
                <TableHead
                  key={axis.id}
                  className="min-w-[94px] text-center text-foreground"
                >
                  {axis.axisLabel}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {parts.map((part) => (
              <TableRow key={part.id}>
                <TableCell>
                  {part.partNo} {part.partName}
                </TableCell>
                {axes.map((axis) => {
                  const cell = cellByKey.get(`${part.id}:${axis.id}`);
                  return (
                    <TableCell key={axis.id} className="text-center">
                      {cell ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="sm"
                              variant={cellStyles[cell.status].variant}
                              className={cellStyles[cell.status].className}
                              onClick={() => onSelectCell(cell)}
                            >
                              {labels[cell.status]}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {cell.changeSummary ||
                              `${part.partName} × ${axis.axisLabel}`}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </table>
      </div>
    </TooltipProvider>
  );
};

export default ChangeMatrixGrid;
